<template>
  <b-modal
    id="subscription-statements-modal"
    ref="modal"
    title="Subscription Statements"
    size="xl"
    hide-footer
    @show="onShow"
  >

    <!-- Filters -->
    <b-card class="mb-3">
      <b-row>
        <b-col sm="6" md="3">
          <b-form-group label="Customer">
            <customer-search-select
              v-model="filters.customer_id"
              placeholder="All customers…"
              @input="loadStatements"
            />
          </b-form-group>
        </b-col>
        <b-col sm="6" md="3">
          <b-form-group label="Package">
            <b-form-select
              v-model="filters.package_id"
              :options="packageOptions"
              @change="loadStatements"
            />
          </b-form-group>
        </b-col>
        <b-col sm="6" md="2">
          <b-form-group label="Payment Method">
            <b-form-select
              v-model="filters.payment_method"
              :options="paymentMethods"
              @change="loadStatements"
            />
          </b-form-group>
        </b-col>
        <b-col sm="6" md="4">
          <b-form-group label="Date range">
            <date-range-picker :auto-apply="true" :ranges="false" :append-to-body="true" v-model="dateRangePicker" class="w-100">
              <template #input="picker">
                <span v-if="picker.startDate && picker.endDate">{{ formatPickerDate(picker.startDate) }} - {{ formatPickerDate(picker.endDate) }}</span>
                <span v-else class="text-muted"><i class="bi bi-calendar-event mr-1"></i>Select date range</span>
              </template>
            </date-range-picker>
          </b-form-group>
        </b-col>
      </b-row>
      <b-button variant="outline-secondary" size="sm" @click="resetFilters">Clear</b-button>
      <b-button variant="outline-secondary" size="sm" class="ml-2" @click="exportStatements">
        <i class="fas fa-download mr-1"></i> Export CSV
      </b-button>
    </b-card>

    <!-- Totals summary -->
    <b-card class="mb-3" v-if="statements.length">
      <b-row class="text-center">
        <b-col md="3" sm="6" class="mb-2">
          <p class="text-muted mb-0">Subscriptions</p>
          <h5 class="font-weight-bold mb-0">{{ statements.length }}</h5>
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <p class="text-muted mb-0">Subtotal</p>
          <h5 class="font-weight-bold mb-0">KES {{ formatAmount(totals.amount) }}</h5>
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <p class="text-muted mb-0">Tax</p>
          <h5 class="font-weight-bold mb-0">KES {{ formatAmount(totals.tax) }}</h5>
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <p class="text-muted mb-0">Total Revenue</p>
          <h5 class="text-primary font-weight-bold mb-0">KES {{ formatAmount(totals.total) }}</h5>
        </b-col>
      </b-row>
    </b-card>

    <!-- Table -->
    <b-card>
      <b-table
          stacked="md"
        :items="statements"
        :fields="fields"
        :busy="loading"
        responsive
        striped
        hover
        foot-clone
      >

        <template #table-busy>
          <div class="text-center text-primary py-4">
            <b-spinner class="mr-2" />Loading…
          </div>
        </template>

        <template #cell(customer)="data">
          <div class="font-weight-bold">{{ data.item.customer && data.item.customer.full_name }}</div>
          <div class="text-muted">{{ data.item.customer && data.item.customer.phone }}</div>
        </template>

        <template #cell(package)="data">
          {{ data.item.package && data.item.package.name }}
        </template>

        <template #cell(date)="data">
          {{ formatDate(data.item.created_at, 'DD-MMM-YYYY') }}
        </template>

        <template #cell(period)="data">
          <span class= "text-muted">
            {{ formatDate(data.item.start_date, 'DD-MM-YY') }} →
            {{ formatDate(data.item.end_date, 'DD-MM-YY') }}
          </span>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant(data.item.status)">
            {{ data.item.status }}
          </b-badge>
        </template>

        <template #cell(amount)="data">
          KES {{ formatAmount(data.item.amount_due || (data.item.package && data.item.package.price)) }}
        </template>

        <template #cell(tax)="data">
          KES {{ formatAmount(calculateTax(data.item)) }}
        </template>

        <template #cell(total)="data">
          KES {{ formatAmount(calculateTotal(data.item)) }}
        </template>

        <template #cell(payment_method)="data">
          <span v-if="data.item.payments && data.item.payments.length">
            {{ data.item.payments[0].payment_method }}
          </span>
          <span v-else class="text-muted">N/A</span>
        </template>

        <!-- Wallet balance — loaded on demand per customer -->
        <template #cell(wallet)="data">
          <span
            v-if="walletBalances[data.item.customer_id] !== undefined"
            class="text-primary font-weight-bold"
          >
            KES {{ formatAmount(walletBalances[data.item.customer_id]) }}
          </span>
          <b-button
            v-else
            variant="link"
            size="sm"
            class="p-0 text-muted"
            @click.stop="loadWalletBalance(data.item.customer_id)"
          >
            <i class="fas fa-wallet mr-1"></i>View
          </b-button>
        </template>

        <!-- Actions -->
        <template #cell(actions)="data">
          <div class="d-flex align-items-center">
            <b-button
              variant="link"
              size="sm"
              class="p-0 mr-2 text-primary"
              title="View subscription"
              @click.stop="viewSubscription(data.item)"
            >
              <i class="fas fa-eye"></i>
            </b-button>
            <b-button
              variant="link"
              size="sm"
              class="p-0 mr-2 text-primary"
              title="Renew subscription"
              @click.stop="renewSubscription(data.item)"
            >
              <i class="fas fa-redo"></i>
            </b-button>
            <b-button
              variant="link"
              size="sm"
              class="p-0 mr-2 text-secondary"
              title="Download PDF receipt"
              :disabled="pdfLoadingId === data.item.id"
              @click.stop="downloadReceipt(data.item)"
            >
              <i v-if="pdfLoadingId === data.item.id" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-download"></i>
            </b-button>
            <b-button
              variant="link"
              size="sm"
              class="p-0 text-warning"
              title="Issue fiscal credit note"
              :disabled="creditNoteLoadingId === data.item.id"
              @click.stop="issueCreditNote(data.item)"
            >
              <i v-if="creditNoteLoadingId === data.item.id" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-receipt"></i>
            </b-button>
          </div>
        </template>

        <!-- Footer totals -->
        <template #foot(customer)>
          <strong>TOTALS</strong>
        </template>
        <template #foot(amount)>
          <strong>KES {{ formatAmount(totals.amount) }}</strong>
        </template>
        <template #foot(tax)>
          <strong>KES {{ formatAmount(totals.tax) }}</strong>
        </template>
        <template #foot(total)>
          <strong class="text-primary">KES {{ formatAmount(totals.total) }}</strong>
        </template>
        <template #foot()>
          <!-- empty footer cells for remaining columns -->
        </template>

      </b-table>
    </b-card>

  </b-modal>
</template>

<script>
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker'
import CustomerSearchSelect from '../../partials/CustomerSearchSelect.vue'

export default {
  name: 'SubscriptionStatementsModal',

  components: { CustomerSearchSelect, DateRangePicker },

  data() {
    return {
      statements:          [],
      walletBalances:      {}, // keyed by customer_id, loaded on demand
      loading:             false,
      pdfLoadingId:        null,
      creditNoteLoadingId: null,

      filters: {
        customer_id:    null,
        package_id:     null,
        payment_method: null,
        date_from:      null,
        date_to:        null,
      },


      packageOptions:  [{ value: null, text: 'All Packages' }],
      paymentMethods: [
        { value: null,           text: 'All Methods' },
        { value: 'cash',         text: 'Cash' },
        { value: 'card',         text: 'Card' },
        { value: 'mobile_money', text: 'Mobile Money' },
        { value: 'wallet',       text: 'Wallet' },
      ],

      fields: [
        { key: 'customer',       label: 'Customer' },
        { key: 'package',        label: 'Package' },
        { key: 'date',           label: 'Date' },
        { key: 'period',         label: 'Period' },
        { key: 'status',         label: 'Status' },
        { key: 'amount',         label: 'Amount' },
        { key: 'tax',            label: 'Tax' },
        { key: 'total',          label: 'Total' },
        { key: 'payment_method', label: 'Payment' },
        { key: 'wallet',         label: 'Wallet Balance' },
        { key: 'actions',        label: '' },
      ],
    }
  },

  computed: {
    totals() {
      return this.statements.reduce((acc, s) => {
        acc.amount += parseFloat(s.amount_due || s.package?.price || 0)
        acc.tax    += this.calculateTax(s)
        acc.total  += this.calculateTotal(s)
        return acc
      }, { amount: 0, tax: 0, total: 0 })
    },
    dateRangePicker: {
      get() {
        return {
          startDate: this.filters.date_from ? new Date(this.filters.date_from) : null,
          endDate: this.filters.date_to ? new Date(this.filters.date_to) : null,
        };
      },
      set(val) {
        this.filters.date_from = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : null;
        this.filters.date_to = val && val.endDate ? moment(val.endDate).format('YYYY-MM-DD') : null;
        this.loadStatements();
      },
    },
  },

  methods: {
    formatPickerDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },

    show() {
      this.$refs.modal.show()
    },

    onShow() {
      this.loadStatements()
      this.loadPackages()
    },

    // ── Data loading ──────────────────────────────────────────────────────

    async loadStatements() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/subscriptions/statements', {
          params: this.filters,
        })
        this.statements     = data.statements || data
        this.walletBalances = {} // clear on reload
      } catch {
        this.$bvToast.toast('Failed to load statements.', { title: 'Error', variant: 'danger' })
      } finally {
        this.loading = false
      }
    },

    async loadPackages() {
      try {
        const { data } = await axios.get('/api/subscription-packages')
        this.packageOptions = [
          { value: null, text: 'All Packages' },
          ...data.map(p => ({ value: p.id, text: p.name })),
        ]
      } catch (e) {
        console.error('Failed to load packages', e)
      }
    },

    async loadWalletBalance(customerId) {
      try {
        const { data } = await axios.get(`/api/subscriptions/wallet/${customerId}`)
        this.$set(this.walletBalances, customerId, parseFloat(data.balance || 0))
      } catch {
        this.$set(this.walletBalances, customerId, 0)
      }
    },

    // ── Row actions ───────────────────────────────────────────────────────

    viewSubscription(subscription) {
      this.$emit('view', subscription)
    },

    renewSubscription(subscription) {
      this.$emit('renew', subscription)
    },

    async downloadReceipt(subscription) {
      if (this.pdfLoadingId === subscription.id) return
      this.pdfLoadingId = subscription.id
      try {
        // Fetch full subscription data (includes tax_snapshot, fiscal_transaction, etc.)
        const { data: s } = await axios.get(`/api/subscriptions/${subscription.id}`)
        const win = window.open('', '_blank')
        win.document.write(this._buildStatementPdf(s))
        win.document.close()
        setTimeout(() => { win.focus(); win.print() }, 500)
      } catch (e) {
        this.$bvToast.toast('Failed to generate receipt PDF.', { title: 'Error', variant: 'danger' })
      } finally {
        this.pdfLoadingId = null
      }
    },

    async issueCreditNote(subscription) {
      if (this.creditNoteLoadingId === subscription.id) return
      const ok = await this.$bvModal.msgBoxConfirm(
        `Issue a fiscal credit note to reverse the receipt for subscription ${subscription.subscription_order || subscription.id}? This cannot be undone.`,
        { title: 'Issue Credit Note', okVariant: 'warning', okTitle: 'Issue Credit Note', cancelTitle: 'Cancel' }
      )
      if (!ok) return
      this.creditNoteLoadingId = subscription.id
      try {
        await axios.post(`/api/subscriptions/${subscription.id}/credit-note`)
        this.$bvToast.toast('Credit note queued successfully.', { title: 'Fiscal', variant: 'success' })
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to issue credit note.'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger' })
      } finally {
        this.creditNoteLoadingId = null
      }
    },

    // ── PDF builder (for direct download from statement row) ─────────────

    _buildStatementPdf(s) {
      const pkg  = s.package  || {}
      const cust = s.customer || {}
      const ft   = s.fiscal_transaction || {}
      const qty  = Math.max(1, parseInt(s.participant_count || 1))
      const gross = parseFloat(pkg.price || 0) * qty
      const disc  = parseFloat(s.discount_amount || 0)
      const net   = this.calculateTotal(s)
      const taxes = (s.tax_snapshot || []).map(t => ({
        name:   t.name || 'Tax',
        rate:   t.perc != null ? t.perc : (t.rate || 0),
        amount: (() => {
          if (t.amount != null && parseFloat(t.amount) > 0) return parseFloat(t.amount)
          const rate = parseFloat(t.perc ?? t.rate ?? 0)
          return t.type === 'inclusive' ? (net / (100 + rate)) * rate : (net * rate) / 100
        })(),
      }))
      const payment = s.payments?.length ? s.payments[s.payments.length - 1] : null
      const esc = v => String(v == null ? '' : v).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      const fmt = v => this.formatAmount(v)
      const fd  = d => this.formatDate(d, 'DD MMM YYYY')

      const fiscalHtml = (ft.control_serial || ft.control_invoice_number) ? `
        <div class="pdf-section fiscal-box">
          <div class="pdf-stitle">Fiscal / TIMS</div>
          <table class="pdf-info"><tbody>
            ${ft.control_datetime       ? `<tr><td>Date/Time</td><td>${esc(ft.control_datetime)}</td></tr>` : ''}
            ${ft.control_invoice_number ? `<tr><td>Invoice #</td><td>${esc(ft.control_invoice_number)}</td></tr>` : ''}
            ${ft.control_serial         ? `<tr><td>Serial</td><td>${esc(ft.control_serial)}</td></tr>` : ''}
            ${ft.control_unit_info      ? `<tr><td>CU Info</td><td>${esc(ft.control_unit_info)}</td></tr>` : ''}
          </tbody></table>
        </div>` : ''

      return `<!DOCTYPE html><html><head><meta charset="utf-8">
        <title>Receipt - ${esc(s.subscription_order || s.id)}</title>
        <style>
          *{box-sizing:border-box;margin:0;padding:0}
          body{font-family:Arial,sans-serif;font-size:12px;color:#222;background:#fff}
          .page{max-width:800px;margin:0 auto;padding:40px}
          @media print{.page{padding:20px}@page{size:A4;margin:15mm}}
          .pdf-header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:2px solid #222;margin-bottom:24px}
          .pdf-co-name{font-size:20px;font-weight:700}
          .pdf-co-meta{font-size:11px;color:#666;line-height:1.7;margin-top:4px}
          .pdf-doc-title{text-align:right}
          .pdf-doc-type{font-size:18px;font-weight:700;text-transform:uppercase}
          .pdf-doc-ref{font-size:11px;color:#666;margin-top:4px}
          .pdf-meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:24px}
          .pdf-section{margin-bottom:20px}
          .pdf-stitle{font-size:10px;text-transform:uppercase;letter-spacing:.8px;color:#888;font-weight:700;margin-bottom:8px;border-bottom:1px solid #eee;padding-bottom:4px}
          .pdf-info{width:100%;border-collapse:collapse}
          .pdf-info td{padding:3px 0;font-size:12px;vertical-align:top}
          .pdf-info td:first-child{color:#666;width:42%;padding-right:8px}
          .pdf-info td:last-child{font-weight:500}
          .pdf-items{width:100%;border-collapse:collapse;margin-bottom:20px}
          .pdf-items th{background:#f5f5f5;padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase;color:#666;border-bottom:2px solid #ddd}
          .pdf-items th:nth-child(2),.pdf-items th:last-child{text-align:right}
          .pdf-items td{padding:10px;border-bottom:1px solid #eee;font-size:12px}
          .pdf-items td:nth-child(2),.pdf-items td:last-child{text-align:right}
          .pdf-items .sub-r td{font-size:11px;color:#666;padding:3px 10px}
          .pdf-totals{margin-left:auto;width:260px}
          .pdf-t-row{display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid #f0f0f0;font-size:12px}
          .pdf-t-row.disc{color:#28a745}
          .pdf-t-row.tax{color:#666;font-size:11px}
          .pdf-t-row.grand{font-weight:700;font-size:15px;border-top:2px solid #222;border-bottom:none;padding-top:8px;margin-top:4px}
          .fiscal-box{border:1px solid #ccc;border-radius:4px;padding:12px;background:#fafafa}
          .pdf-footer{margin-top:32px;border-top:1px solid #eee;padding-top:10px;text-align:center;font-size:10px;color:#999}
          .badge{display:inline-block;padding:2px 8px;border-radius:3px;font-size:10px;font-weight:700;text-transform:uppercase}
          .b-active{background:#d4edda;color:#155724}.b-expired{background:#e2e3e5;color:#383d41}
          .b-cancelled{background:#fff3cd;color:#856404}.b-past_due{background:#f8d7da;color:#721c24}
        </style></head><body><div class="page">
        <div class="pdf-header">
          <div>
            <div class="pdf-co-name">${esc(this.$store?.getters?.userDetails?.company?.name || '')}</div>
            <div class="pdf-co-meta">
              ${this.$store?.getters?.userDetails?.company?.address ? esc(this.$store.getters.userDetails.company.address) + '<br>' : ''}
              ${this.$store?.getters?.userDetails?.company?.phone   ? 'Tel: ' + esc(this.$store.getters.userDetails.company.phone) + '<br>' : ''}
              ${this.$store?.getters?.userDetails?.company?.krapin  ? 'PIN: ' + esc(this.$store.getters.userDetails.company.krapin) : ''}
            </div>
          </div>
          <div class="pdf-doc-title">
            <div class="pdf-doc-type">Subscription Receipt</div>
            <div class="pdf-doc-ref">${esc(s.subscription_order || 'SUB-' + String(s.id || '').substring(0,8).toUpperCase())}</div>
            <div style="font-size:11px;color:#666">Date: ${fd(s.created_at)}</div>
          </div>
        </div>
        <div class="pdf-meta-grid">
          <div class="pdf-section">
            <div class="pdf-stitle">Customer</div>
            <table class="pdf-info"><tbody>
              <tr><td>Name</td><td>${esc(cust.full_name || '—')}</td></tr>
              ${cust.phone  ? `<tr><td>Phone</td><td>${esc(cust.phone)}</td></tr>` : ''}
              ${cust.email  ? `<tr><td>Email</td><td>${esc(cust.email)}</td></tr>` : ''}
              ${cust.krapin ? `<tr><td>KRA PIN</td><td>${esc(cust.krapin)}</td></tr>` : ''}
            </tbody></table>
          </div>
          <div class="pdf-section">
            <div class="pdf-stitle">Subscription</div>
            <table class="pdf-info"><tbody>
              <tr><td>Reference</td><td>${esc(s.subscription_order || '—')}</td></tr>
              <tr><td>Status</td><td><span class="badge b-${esc(s.status)}">${esc(s.status)}</span></td></tr>
              <tr><td>Start</td><td>${fd(s.start_date)}</td></tr>
              <tr><td>End</td><td>${fd(s.end_date)}</td></tr>
            </tbody></table>
          </div>
        </div>
        <div class="pdf-section">
          <div class="pdf-stitle">Line Items</div>
          <table class="pdf-items">
            <thead><tr><th>Description</th><th>Unit Price</th><th>Qty</th><th>Amount</th></tr></thead>
            <tbody>
              <tr>
                <td>${esc(pkg.name || '')}</td>
                <td>KES ${fmt(pkg.price)}</td>
                <td>${qty}${qty > 1 ? ' participants' : ''}</td>
                <td>KES ${fmt(gross)}</td>
              </tr>
              ${disc > 0 ? `<tr class="sub-r"><td colspan="3">Discount${s.discount_reason ? ' — ' + esc(s.discount_reason) : ''}</td><td style="color:#28a745">−&nbsp;KES ${fmt(disc)}</td></tr>` : ''}
            </tbody>
          </table>
        </div>
        <div class="pdf-totals">
          ${disc > 0 ? `<div class="pdf-t-row"><span>Gross Subtotal</span><span>KES ${fmt(gross)}</span></div>
          <div class="pdf-t-row disc"><span>Discount</span><span>−&nbsp;KES ${fmt(disc)}</span></div>` : ''}
          <div class="pdf-t-row"><span>Net Amount</span><span>KES ${fmt(net)}</span></div>
          ${taxes.map(t => `<div class="pdf-t-row tax"><span>${esc(t.name)} (${t.rate}%) [incl.]</span><span>KES ${fmt(t.amount)}</span></div>`).join('')}
          <div class="pdf-t-row grand"><span>TOTAL</span><span>KES ${fmt(net)}</span></div>
          ${payment ? `<div class="pdf-t-row" style="margin-top:8px;font-size:11px;color:#666"><span>Paid via</span><span>${esc((payment.payment_method || '').toUpperCase())}</span></div>` : ''}
        </div>
        ${fiscalHtml}
        <div class="pdf-footer">Thank you for your subscription! &nbsp;·&nbsp; Generated ${new Date().toLocaleString('en-KE')}</div>
      </div></body></html>`
    },

    // ── Tax / total helpers ───────────────────────────────────────────────

    calculateTax(s) {
      if (!s.tax_snapshot?.length) return 0
      // Use stored amounts when available (accurate for discounted subscriptions)
      const hasStoredAmounts = s.tax_snapshot.some(t => t.amount != null)
      if (hasStoredAmounts) {
        return s.tax_snapshot.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)
      }
      // Legacy fallback: recalculate from amount_due and rate
      const total = this.calculateTotal(s)
      return s.tax_snapshot.reduce((sum, tax) => {
        const rate = parseFloat(tax.perc ?? tax.rate ?? 0)
        return sum + (tax.type === 'inclusive'
          ? (total / (100 + rate)) * rate
          : (total * rate) / 100)
      }, 0)
    },

    calculateTotal(s) {
      // amount_due is the frozen net payable (after discount, taxes included)
      const due = parseFloat(s.amount_due || 0)
      return due > 0 ? due : parseFloat(s.package?.price || 0)
    },

    // ── Filters ───────────────────────────────────────────────────────────

    resetFilters() {
      this.filters = {
        customer_id: null, package_id: null,
        payment_method: null, date_from: null, date_to: null,
      }
      this.loadStatements()
    },

    // ── Export ────────────────────────────────────────────────────────────

    exportStatements() {
      let csv = 'Customer,Phone,Package,Date,Start,End,Status,Amount,Tax,Total,Payment Method\n'

      this.statements.forEach(s => {
        csv += [
          s.customer?.full_name  || '',
          s.customer?.phone      || '',
          s.package?.name        || '',
          this.formatDate(s.created_at, 'DD-MM-YYYY'),
          this.formatDate(s.start_date,  'DD-MM-YYYY'),
          this.formatDate(s.end_date,    'DD-MM-YYYY'),
          s.status,
          parseFloat(s.amount_due || s.package?.price || 0).toFixed(2),
          this.calculateTax(s).toFixed(2),
          this.calculateTotal(s).toFixed(2),
          s.payments?.length ? s.payments[0].payment_method : 'N/A',
        ].join(',') + '\n'
      })

      csv += `\nTOTALS,,,,,,,${this.totals.amount.toFixed(2)},${this.totals.tax.toFixed(2)},${this.totals.total.toFixed(2)}\n`

      const blob = new Blob([csv], { type: 'text/csv' })
      const url  = window.URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `statements-${moment().format('YYYY-MM-DD')}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    },

    // ── Misc ──────────────────────────────────────────────────────────────

    formatAmount(v) {
      return parseFloat(v || 0).toLocaleString('en-KE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })
    },

    formatDate(d, fmt = 'DD-MM-YYYY') {
      return d ? moment(d).format(fmt) : '—'
    },

    getStatusVariant(s) {
      return {
        active:    'success',
        expired:   'secondary',
        cancelled: 'warning',
        past_due:  'danger',
      }[s] || 'secondary'
    },
  },
}
</script>
